import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Properties API
export const propertiesAPI = {
  list: () => api.get('/properties'),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  syncChannex: (id) => api.post(`/properties/${id}/sync-channex`),
  testChannex: (id) => api.post(`/properties/${id}/test-channex`),
  getUsers: (id) => api.get(`/properties/${id}/users`),
  updateUsers: (id, userIds) => api.put(`/properties/${id}/users`, userIds),
};

// Rooms API
export const roomsAPI = {
  list: (propertyId) => api.get('/rooms', { params: { property_id: propertyId } }),
  get: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
};

// Bookings API
export const bookingsAPI = {
  list: (params) => api.get('/bookings', { params }),
  get: (id) => api.get(`/bookings/${id}`),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, null, { params: { new_status: status } }),
  retryChannex: (id) => api.post(`/bookings/${id}/retry-channex`),
  daily: (dateStr, propertyId) => api.get(`/bookings/daily/${dateStr}`, { params: propertyId ? { property_id: propertyId } : {} }),
  checkin: (id, data) => api.put(`/bookings/${id}/checkin`, data),
  undoCheckin: (id) => api.put(`/bookings/${id}/undo-checkin`),
  checkout: (id) => api.put(`/bookings/${id}/checkout`),
  noShow: (id) => api.put(`/bookings/${id}/no-show`),
  cancel: (id) => api.put(`/bookings/${id}/cancel`),
  amend: (id, data) => api.put(`/bookings/${id}/amend`, data),
  getHistory: (id) => api.get(`/bookings/${id}/history`),
  transfer: (id, data) => api.post(`/bookings/${id}/transfer`, data),
  getReceipt: (id) => api.get(`/bookings/${id}/receipt`),
  toggleCleaned: (id) => api.put(`/bookings/${id}/cleaned`),
};

// Public API
export const publicAPI = {
  listProperties: () => api.get('/public/properties'),
  getProperty: (id) => api.get(`/public/properties/${id}`),
  getPropertyBySlug: (slug) => api.get(`/public/property/${slug}`),
  searchAvailability: (data) => api.post('/public/search', data),
  getRoom: (id) => api.get(`/public/rooms/${id}`),
  createBooking: (data) => api.post('/public/bookings', data),
  getBookingByReference: (ref) => api.get(`/public/bookings/${ref}`),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Images API
export const imagesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUrl: (storagePath) => `${API_BASE}/images/serve/${storagePath}`,
};

// Users API (Super Admin)
export const usersAPI = {
  list: () => api.get('/users'),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
  assignProperties: (userId, properties) => api.put(`/users/${userId}/properties`, properties),
  toggle: (userId) => api.put(`/users/${userId}/toggle`),
  remove: (userId) => api.delete(`/users/${userId}`),
};

// Payments API
export const paymentsAPI = {
  checkStatus: (bookingRef) => api.get(`/payments/status/${bookingRef}`),
  getProfile: (propertyId) => api.get(`/payments/profile/${propertyId}`),
};

// Channex Webhook API
export const channexAPI = {
  registerWebhook: (propertyId) => api.post(`/channex/register-webhook/${propertyId}`),
  listWebhooks: (propertyId) => api.get(`/channex/webhooks/${propertyId}`),
  removeWebhook: (propertyId, webhookId) => api.delete(`/channex/webhooks/${propertyId}/${webhookId}`),
  syncBookings: (propertyId) => api.post(`/channex/sync-bookings/${propertyId}`),
  getWebhookLogs: (propertyId, limit = 50) => api.get(`/channex/webhook-logs/${propertyId}`, { params: { limit } }),
};

// Diary API
export const diaryAPI = {
  get: (propertyId, startDate, days = 14) =>
    api.get('/diary', { params: { property_id: propertyId, start_date: startDate, days } }),
};

// Calendar API
export const calendarAPI = {
  get: (startMonth, months = 9, propertyId) =>
    api.get('/calendar', { params: { start_month: startMonth, months, property_id: propertyId || undefined } }),
};

// Walk-in Bookings
export const walkInAPI = {
  create: (data) => api.post('/bookings/walk-in', data),
};

// Guests API
export const guestsAPI = {
  list: (propertyId) => api.get('/guests', { params: { property_id: propertyId } }),
  search: (propertyId, q) => api.get('/guests/search', { params: { property_id: propertyId, q } }),
  get: (guestId) => api.get(`/guests/${guestId}`),
  delete: (guestId) => api.delete(`/guests/${guestId}`),
  register: (propertySlug, data) => api.post(`/public/register/${propertySlug}`, data),
  getPropertyInfo: (propertySlug) => api.get(`/public/register/${propertySlug}`),
};

// Ledger API
export const ledgerAPI = {
  recordPayment: (data) => api.post('/ledger/payments', data),
  listPayments: (params) => api.get('/ledger/payments', { params }),
  voidPayment: (id) => api.delete(`/ledger/payments/${id}`),
  getBookingLedger: (bookingId) => api.get(`/ledger/booking/${bookingId}`),
  getOutstanding: (params) => api.get('/ledger/outstanding', { params }),
  // Payment Accounts
  listAccounts: (params) => api.get('/ledger/accounts', { params }),
  createAccount: (data) => api.post('/ledger/accounts', data),
  updateAccount: (id, data) => api.put(`/ledger/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/ledger/accounts/${id}`),
  getAccountLedger: (id) => api.get(`/ledger/accounts/${id}/ledger`),
  getAccountsSummary: (params) => api.get('/ledger/accounts/summary/all', { params }),
};

// Dashboard Stats API
export const dashboardAPI = {
  salesAnalytics: (propertyId) => api.get('/dashboard/sales-analytics', { params: propertyId ? { property_id: propertyId } : {} }),
  recentReservations: (propertyId, limit = 10) => api.get('/dashboard/recent-reservations', { params: { ...(propertyId ? { property_id: propertyId } : {}), limit } }),
};

export default api;
